import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ref, onValue } from 'firebase/database';
import { database } from './firebaseConfig';
import { getStoredChatId } from './chatId';
import { hasPremium } from './subscription';
import './MainPage.css';
import Transactions, { StatementPlaceholder } from './Transactions';
import { readCachedTransactions, cacheTransactions } from './txCache';
import { readCachedUser, cacheUser } from './userCache';
import ImageWithCache from './ImageWithCache'; // <-- new import
import CardDesign, { CardDepth } from './CardDesign';
import CardPopup from './CardPopup';
import AllCardsPopup from './AllCardsPopup';
import OpenCardScreen from './OpenCardScreen';
import TransferScreen from './TransferScreen';
import BuyCurrencyScreen from './BuyCurrencyScreen';
import { fxSpentThisMonth } from './fxStore';
import useAnimatedNumber, { AnimatedNumber } from './useAnimatedNumber';
import {
  useUserCards, useCardsOrder, saveCardsOrder, sortByOrder, formatMoney, cardIcon, MAIN_CARD_ID,
  DEFAULT_DAILY_LIMIT, closeCard,
} from './cardsStore';
import seededRandom from './seededRandom';
import { useBalancesHidden, useMaskBalance } from './balanceVisibility';
import { useLogout } from './logout';

const RATES_CACHE_KEY = 'currency_rates_cache';
// Крок між індикаторами в плашці пейджера (центр до центру), зі скріншота
const PAGER_DOT_STEP = 18;

const TRANSLIT_MAP = {
  а: 'a', б: 'b', в: 'v', г: 'h', ґ: 'g', д: 'd', е: 'e', є: 'ie', ж: 'zh', з: 'z',
  и: 'y', і: 'i', ї: 'i', й: 'i', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p',
  р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch',
  ь: '', ю: 'iu', я: 'ia', ё: 'e', ъ: '', ы: 'y', э: 'e', "'": '', '’': '',
};

function transliterate(word) {
  return word
    .toLowerCase()
    .split('')
    .map((ch) => (ch in TRANSLIT_MAP ? TRANSLIT_MAP[ch] : ch))
    .join('');
}

// Стабільна псевдовипадковість, прив'язана до seed (наприклад chatId),
// щоб номер/пошта не мінялись на кожному ре-рендері
function generatePhone(seed) {
  if (!seed) return '—';
  const rand = seededRandom(`${seed}-phone`);
  let digits = '';
  for (let i = 0; i < 7; i++) {
    digits += Math.floor(rand() * 10);
  }
  return `+38068${digits}`;
}

function generateEmail(pib) {
  const parts = (pib || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const [surname, firstName] = parts; // ПІБ у форматі "Прізвище Ім'я"
  const local = [transliterate(firstName || ''), transliterate(surname || '')]
    .filter(Boolean)
    .join('.');
  return local ? `${local}@gmail.com` : '—';
}

function generateCardholderName(pib) {
  const parts = (pib || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const [surname, firstName] = parts; // ПІБ у форматі "Прізвище Ім'я"
  return [transliterate(firstName || ''), transliterate(surname || '')]
    .filter(Boolean)
    .join(' ')
    .toUpperCase();
}

// Кнопка «Купити …» на сторінці валютної картки
const BUY_CURRENCY = {
  USD: { icon: '/buy-usd-btn.png', label: 'долари' },
  EUR: { icon: '/buy-eur-btn.png', label: 'євро' },
};
// Кнопки під карткою (і «+» біля балансу) у колір картки — вирізані зі
// скріншотів monobank (public/btn-<тип>/); для решти — стандартні
const BTN_SKINS = { eur: '/btn-eur', usd: '/btn-usd', white: '/btn-white' };
const btnSrc = (type, name, fallback) => (BTN_SKINS[type] ? `${BTN_SKINS[type]}/${name}.png` : fallback);

const currencyLabel = (currency) => (currency && currency !== 'UAH' ? currency : undefined);

const MainPage = () => {
  const navigate = useNavigate();
  const logout = useLogout();
  const chatId = getStoredChatId();
  const balanceKey = `balance_raw_${chatId}`;
  const [balancesHidden, toggleBalancesHidden] = useBalancesHidden();
  const maskBalance = useMaskBalance();
  // Подвійний тап по балансу ховає/показує всі баланси
  const lastBalanceTapRef = useRef(0);
  function handleBalanceTap() {
    const now = Date.now();
    if (now - lastBalanceTapRef.current < 350) {
      lastBalanceTapRef.current = 0;
      toggleBalancesHidden();
    } else {
      lastBalanceTapRef.current = now;
    }
  }
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarModalClosing, setAvatarModalClosing] = useState(false);
  const avatarModalTimeoutRef = useRef(null);
  // Стартуємо з кешу (його заповнили PasswordScreen і прелоад до входу),
  // щоб картка й ім'я власника були справжніми вже на першому кадрі.
  const [userPib, setUserPib] = useState(() => readCachedUser(chatId).pib);
  const [userPhoto, setUserPhoto] = useState(() => readCachedUser(chatId).photoUrl);
  const [userCardNumber, setUserCardNumber] = useState(() => readCachedUser(chatId).cardNumber);
  // Преміум: перекази на картки й відкриття нових карток (без нього кнопки не реагують)
  const [premium, setPremium] = useState(false);

  useEffect(() => {
    if (!chatId) return;
    const userRef = ref(database, `users/${chatId}`);
    const unsubUser = onValue(userRef, (snapshot) => {
      setPremium(hasPremium(snapshot.val()));
      const user = cacheUser(chatId, snapshot.val() || {});
      setUserPib(user.pib);
      setUserPhoto(user.photoUrl);
      setUserCardNumber(user.cardNumber);
    });
    return () => unsubUser();
  }, [chatId]);

  const AVATAR_MODAL_ANIM_MS = 300;

  function closeAvatarModal() {
    setAvatarModalClosing(true);
    avatarModalTimeoutRef.current = setTimeout(() => {
      setAvatarModalOpen(false);
      setAvatarModalClosing(false);
    }, AVATAR_MODAL_ANIM_MS);
  }

  // Попап картки: зберігаємо DOM-вузол картки, з якого вона «вилітає»
  // { el, card } — DOM-вузол картки, з якого «вилітає» попап, і сама картка
  const [cardPopup, setCardPopup] = useState(null);

  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [documentsModalClosing, setDocumentsModalClosing] = useState(false);
  const documentsModalTimeoutRef = useRef(null);
  const DOCUMENTS_MODAL_ANIM_MS = 300;

  function openDocumentsModal() {
    setDocumentsModalOpen(true);
  }

  function closeDocumentsModal() {
    setDocumentsModalClosing(true);
    documentsModalTimeoutRef.current = setTimeout(() => {
      setDocumentsModalOpen(false);
      setDocumentsModalClosing(false);
    }, DOCUMENTS_MODAL_ANIM_MS);
  }

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsModalClosing, setSettingsModalClosing] = useState(false);
  const settingsModalTimeoutRef = useRef(null);
  const SETTINGS_MODAL_ANIM_MS = 300;

  function openSettingsModal() {
    setSettingsModalOpen(true);
  }

  function closeSettingsModal() {
    setSettingsModalClosing(true);
    settingsModalTimeoutRef.current = setTimeout(() => {
      setSettingsModalOpen(false);
      setSettingsModalClosing(false);
    }, SETTINGS_MODAL_ANIM_MS);
  }

  const [appSettings, setAppSettings] = useState({
    appleSignIn: true,
    googleSignIn: false,
    showCents: true,
    catRewards: true,
    showCalculator: true,
    showInContacts: true,
    pinRecoveryBlock: false,
    systemTextSize: true,
  });

  function toggleAppSetting(key) {
    setAppSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  useEffect(() => {
    return () => {
      if (avatarModalTimeoutRef.current) clearTimeout(avatarModalTimeoutRef.current);
      if (documentsModalTimeoutRef.current) clearTimeout(documentsModalTimeoutRef.current);
      if (settingsModalTimeoutRef.current) clearTimeout(settingsModalTimeoutRef.current);
    };
  }, []);

  function formatBalance(value) {
    const [intPart, decimalPart] = Number(value)
      .toFixed(2) // всегда два знака после запятой
      .split('.');

    const intWithSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return `${intWithSpaces}.${decimalPart} ₴`;
  }

  function formatRate(value) {
    return value !== null && value !== undefined ? Number(value).toFixed(2) : '—';
  }

  // синхронное чтение кеша для мгновенного отображения
  const [balanceValue, setBalanceValue] = useState(() => {
    try {
      const cached = localStorage.getItem(balanceKey);
      if (cached !== null && cached !== undefined) {
        return Number(cached);
      }
    } catch (e) { /* ignore */ }
    return 0;
  });


  // Шапка (аватар/лого) плавно зникає при прокрутці вниз і повертається
  // при прокрутці вгору. Прозорість прив'язана до scrollTop контейнера:
  // 0px → opacity 1, HEADER_FADE_DISTANCE → opacity 0.
  const headerConRef = useRef(null);
  const HEADER_FADE_DISTANCE = 40;
  // Відкриті додаткові картки з БД (cards/{chatId}) — між карткою
  // користувача і «Відкрити картку»
  const openedCards = useUserCards(chatId);
  const cardsOrder = useCardsOrder(chatId);
  // Усі картки в порядку з «Усі картки» (cardsOrder/{chatId}), основна — теж
  const pagerCards = useMemo(
    () => sortByOrder([{ id: MAIN_CARD_ID, type: MAIN_CARD_ID }, ...openedCards], cardsOrder),
    [openedCards, cardsOrder],
  );
  const pageCount = pagerCards.length + 1;
  const pagerIdsKey = pagerCards.map((c) => c.id).join(',');
  // Пейджер карток: 0 — картка користувача, далі відкриті картки,
  // останній — «Відкрити картку».
  // Жест обробляємо самі (а не нативним скролом): сторінки, стрічка карток,
  // фон і точки рухаються в одному кадрі requestAnimationFrame, тож нічого
  // не відстає на кадр і не смикається. React під час свайпу не
  // перерендерюється — усе через стилі/класи напряму.
  const cardPagerRef = useRef(null);
  const cardPagesRef = useRef(null);
  const cardTrackRef = useRef(null);
  const cardPageRefs = useRef([]); // сторінки за індексом
  const heroBgRefs = useRef([]); // шари фону за індексом сторінки (у 0-ї — фон .hero-con)
  const pagerPillRef = useRef(null);
  const pagerDotRefs = useRef([]); // точки карток і «+» за індексом сторінки
  const pageCountRef = useRef(pageCount);
  pageCountRef.current = pageCount;
  const pagerRef = useRef({
    progress: 0, // 0..pageCount-1 (з гумовим виходом за межі під час перетягування)
    index: 0,
    heights: [],
    drag: null,
    anim: 0, // таймер завершення CSS-доводки
    frame: 0, // rAF під час перетягування
    dotsTimer: 0,
    dotsWindow: 0, // індекс лівого з двох видимих індикаторів у плашці
    suppressClick: false,
  });

  function applyCardProgress(p) {
    const pager = cardPagerRef.current;
    if (!pager) return;
    const w = pager.clientWidth;
    if (cardPagesRef.current) cardPagesRef.current.style.transform = `translate3d(${-p * w}px, 0, 0)`;
    const track = cardTrackRef.current;
    if (track && track.lastElementChild) {
      track.style.transform = `translate3d(${-p * track.lastElementChild.offsetWidth}px, 0, 0)`;
    }
    const clamped = Math.max(0, Math.min(pageCountRef.current - 1, p));
    // Шари фону лежать за порядком сторінок: нижчий з пари сусідів —
    // повністю, вищий проявляється над ним — тоді кольори перетікають
    // напряму, без «брудної» середини
    const i = Math.floor(clamped);
    const f = clamped - i;
    heroBgRefs.current.forEach((el, k) => {
      if (el) el.style.opacity = String(k === i ? 1 : k === i + 1 ? f : 0);
    });
    applyPagerDots(Math.round(clamped));
  }

  // Плашка показує лише два сусідні індикатори, як page control у monobank:
  // вікно зсувається, тільки коли активна сторінка виходить за нього —
  // вправо активний стоїть праворуч, назад — ліворуч. Той, що входить,
  // виростає з нуля, той, що виходить, стискається (transition у CSS).
  function applyPagerDots(idx) {
    const st = pagerRef.current;
    const maxWindow = Math.max(0, pageCountRef.current - 2);
    let w = st.dotsWindow;
    if (idx > w + 1) w = idx - 1;
    if (idx < w) w = idx;
    w = Math.min(Math.max(w, 0), maxWindow);
    st.dotsWindow = w;
    pagerDotRefs.current.forEach((el, k) => {
      if (!el) return;
      const rel = k - w;
      const visible = rel === 0 || rel === 1;
      el.style.transform = `translate3d(${rel * PAGER_DOT_STEP}px, 0, 0) scale(${visible ? 1 : 0})`;
      el.classList.toggle('active', k === idx);
    });
  }

  // Висота пейджера = висота активної сторінки (інакше під «Відкрити
  // картку» лишалась би порожнеча). Міняємо лише коли свайп завершено.
  function applyPagerHeight() {
    const st = pagerRef.current;
    const h = st.heights[st.index];
    if (cardPagerRef.current && h) cardPagerRef.current.style.height = `${h}px`;
  }

  function showPagerDots() {
    const st = pagerRef.current;
    clearTimeout(st.dotsTimer);
    if (pagerPillRef.current) pagerPillRef.current.classList.add('dots');
  }
  // після зупинки плашка повертається до «Усі картки»
  function scheduleHidePagerDots() {
    const st = pagerRef.current;
    clearTimeout(st.dotsTimer);
    st.dotsTimer = setTimeout(() => {
      if (pagerPillRef.current) pagerPillRef.current.classList.remove('dots');
    }, 1300);
  }

  // Доводка до сторінки — CSS-переходом (композитинг на GPU, як карусель
  // статистики), а не покадровим rAF: так не смикається на iOS. Тривалість
  // залежить від відстані.
  const CARD_EASE = 'cubic-bezier(0.25, 1, 0.5, 1)';
  function setCardTransition(ms) {
    const value = ms ? `transform ${ms}ms ${CARD_EASE}` : 'none';
    [cardPagesRef.current, cardTrackRef.current].forEach((el) => { if (el) el.style.transition = value; });
    heroBgRefs.current.forEach((el) => { if (el) el.style.transition = 'none'; });
  }

  // Фон під час доводки: якщо обидва шари одночасно гаснуть/проявляються,
  // посередині просвічує те, що під ними (смуга). Тож цільовий шар або
  // проявляється поверх усіх видимих, або (коли над ним є видимі) одразу
  // стає непрозорим під ними, а верхні гаснуть. Шари лежать у порядку сторінок.
  function fadeHeroBgTo(idx, ms) {
    const layers = heroBgRefs.current;
    const visibleAbove = layers.some((el, k) => el && k > idx && Number(el.style.opacity) > 0);
    const fade = `opacity ${ms}ms ${CARD_EASE}`;
    layers.forEach((el, k) => {
      if (!el) return;
      if (k === idx) {
        el.style.transition = visibleAbove ? 'none' : fade;
        el.style.opacity = '1';
      } else if (k > idx) {
        el.style.transition = fade;
        el.style.opacity = '0';
      }
      // нижчі шари не чіпаємо — їх накриє цільовий; приберемо в кінці
    });
  }
  // Зупиняє доводку на місці: поточну позицію читаємо з обчисленого transform
  function stopCardTransition() {
    const st = pagerRef.current;
    if (!st.anim) { setCardTransition(0); return; }
    clearTimeout(st.anim);
    st.anim = 0;
    const pages = cardPagesRef.current;
    const w = cardPagerRef.current ? cardPagerRef.current.clientWidth : 0;
    if (pages && w) {
      const m = new DOMMatrixReadOnly(getComputedStyle(pages).transform);
      st.progress = -m.m41 / w;
    }
    setCardTransition(0);
    applyCardProgress(st.progress);
  }

  function animateCardTo(idx) {
    const st = pagerRef.current;
    cancelAnimationFrame(st.frame); // кадр перетягування не має перебити доводку
    stopCardTransition();
    const from = st.progress;
    const dist = Math.abs(idx - from);
    st.progress = idx;
    st.index = idx;
    if (dist < 0.001) {
      applyCardProgress(idx);
      applyPagerHeight();
      scheduleHidePagerDots();
      return;
    }
    const duration = Math.round(220 + 230 * Math.min(1, dist));
    setCardTransition(duration);
    const pager = cardPagerRef.current;
    const w = pager ? pager.clientWidth : 0;
    if (cardPagesRef.current) cardPagesRef.current.style.transform = `translate3d(${-idx * w}px, 0, 0)`;
    const track = cardTrackRef.current;
    if (track && track.lastElementChild) {
      track.style.transform = `translate3d(${-idx * track.lastElementChild.offsetWidth}px, 0, 0)`;
    }
    fadeHeroBgTo(idx, duration);
    applyPagerDots(idx);
    // transition не знімаємо в кінці (інакше iOS перемальовує шар і текст
    // смикається) — його вимкне наступне перетягування
    st.anim = setTimeout(() => {
      st.anim = 0;
      heroBgRefs.current.forEach((el, k) => {
        if (el) { el.style.transition = 'none'; el.style.opacity = k === idx ? '1' : '0'; }
      });
      applyPagerHeight();
      scheduleHidePagerDots();
    }, duration + 20);
  }

  // Попап «Усі картки»
  const [allCardsOpen, setAllCardsOpen] = useState(false);
  // Екран «Відкрити картку»
  const [openCardOpen, setOpenCardOpen] = useState(false);
  // Відкрити нову картку — лише з Преміумом; false — щоб виклик (напр. «Усі картки») теж нічого не робив
  const openNewCard = () => {
    if (!premium) return false;
    setOpenCardOpen(true);
    return true;
  };
  // Екран «Переказ на картку» — id картки, з якої його відкрили
  const [transferFrom, setTransferFrom] = useState(null);
  // «Купити долари / євро» — валютна картка, на яку купуємо
  const [buyFor, setBuyFor] = useState(null);
  // Баланси на екрані «докручуються» до нового значення при будь-якій зміні.
  // Поки відкрито переказ, анімацію притримуємо — її видно після «Готово»
  const balancesPaused = !!transferFrom || !!buyFor;
  const displayBalance = useAnimatedNumber(balanceValue, balancesPaused);

  function goToCardSlide(idx) {
    showPagerDots();
    animateCardTo(idx);
  }

  function handlePagerPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const st = pagerRef.current;
    st.drag = {
      id: e.pointerId,
      x0: e.clientX,
      y0: e.clientY,
      p0: st.progress,
      locked: null, // 'x' | 'y'
      samples: [{ x: e.clientX, t: performance.now() }],
    };
  }

  function handlePagerPointerMove(e) {
    const st = pagerRef.current;
    const d = st.drag;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.locked) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      d.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (d.locked === 'y') return;
      // перехоплюємо свайп: зупиняємо доводку, що могла ще йти
      stopCardTransition();
      d.p0 = st.progress;
      d.x0 = e.clientX;
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      showPagerDots();
    }
    if (d.locked !== 'x') return;
    const w = cardPagerRef.current ? cardPagerRef.current.clientWidth : 1;
    let p = d.p0 - (e.clientX - d.x0) / w;
    const last = pageCountRef.current - 1;
    // гумовий опір за краями
    if (p < 0) p *= 0.3;
    if (p > last) p = last + (p - last) * 0.3;
    st.progress = p;
    const now = performance.now();
    d.samples.push({ x: e.clientX, t: now });
    while (d.samples.length > 2 && now - d.samples[0].t > 100) d.samples.shift();
    cancelAnimationFrame(st.frame);
    st.frame = requestAnimationFrame(() => applyCardProgress(st.progress));
  }

  function handlePagerPointerUp(e) {
    const st = pagerRef.current;
    const d = st.drag;
    if (!d || d.id !== e.pointerId) return;
    st.drag = null;
    if (d.locked !== 'x') return;
    // після горизонтального свайпу тап по картці не має спрацювати
    st.suppressClick = true;
    setTimeout(() => { st.suppressClick = false; }, 0);
    const first = d.samples[0];
    const last = d.samples[d.samples.length - 1];
    const v = (last.x - first.x) / Math.max(1, last.t - first.t); // px/ms, >0 — вправо
    const lastPage = pageCountRef.current - 1;
    let target = Math.round(Math.max(0, Math.min(lastPage, st.progress)));
    if (Math.abs(v) > 0.3) target = v < 0 ? Math.min(lastPage, st.index + 1) : Math.max(0, st.index - 1);
    animateCardTo(target);
  }

  function handlePagerClickCapture(e) {
    if (pagerRef.current.suppressClick) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  // Картки переставили (або додали): лишаємось на тій самій картці, а не
  // на тому ж номері сторінки
  const prevPagerIdsRef = useRef(null);
  useLayoutEffect(() => {
    const ids = [...pagerCards.map((c) => c.id), 'new'];
    const prev = prevPagerIdsRef.current;
    prevPagerIdsRef.current = ids;
    if (!prev) return;
    const st = pagerRef.current;
    let idx = ids.indexOf(prev[st.index]);
    // картку, на якій стояли, закрили — показуємо попередню
    if (idx === -1) idx = Math.max(0, Math.min(st.index - 1, ids.length - 1));
    if (idx !== st.index) {
      clearTimeout(st.anim);
      st.anim = 0;
      setCardTransition(0);
      st.index = idx;
      st.progress = idx;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagerIdsKey]);

  useLayoutEffect(() => {
    const st = pagerRef.current;
    const measure = () => {
      st.heights = cardPageRefs.current.slice(0, pageCount).map((el) => (el ? el.scrollHeight : 0));
      applyPagerHeight();
      applyCardProgress(st.progress);
    };
    measure();
    window.addEventListener('resize', measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      cardPageRefs.current.slice(0, pageCount).forEach((el) => el && ro.observe(el));
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagerIdsKey]);

  useEffect(() => () => {
    const st = pagerRef.current;
    clearTimeout(st.anim);
    cancelAnimationFrame(st.frame);
    clearTimeout(st.dotsTimer);
  }, []);

  // Щойно відкрита картка: як тільки вона прийде з БД і її сторінка
  // відрендериться — гортаємо до неї
  const pendingCardIdRef = useRef(null);
  useEffect(() => {
    if (pendingCardIdRef.current === null) return;
    const idx = pagerCards.findIndex((c) => c.id === pendingCardIdRef.current);
    if (idx === -1) return;
    pendingCardIdRef.current = null;
    goToCardSlide(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagerCards]);

  // CTA «Хочу … картку»: картку вже записано в БД (OpenCardScreen)
  function handleCardOpened(cardId) {
    const idx = pagerCards.findIndex((c) => c.id === cardId);
    if (idx !== -1) goToCardSlide(idx);
    else pendingCardIdRef.current = cardId;
  }

  function handleMainScroll(e) {
    if (!headerConRef.current) return;
    const st = e.currentTarget.scrollTop;
    const opacity = Math.max(0, Math.min(1, 1 - st / HEADER_FADE_DISTANCE));
    headerConRef.current.style.opacity = String(opacity);
  }


  const [transactions, setTransactions] = useState(() => readCachedTransactions(chatId) || []);

  // курсы валют кладутся в кеш один раз за вход в приложение (App.jsx),
  // здесь только читаем — без собственного запроса
  const rates = (() => {
    try {
      const cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY));
      if (cached) return cached;
    } catch (e) { /* ignore */ }
    return { usdBuy: null, usdSell: null, eurBuy: null, eurSell: null };
  })();

  // Курсы валют больше не запрашиваются здесь — публичное API monobank
  // жёстко лимитирует частоту, поэтому запрос идёт один раз за вход в
  // приложение (в App.jsx, вместе с preload баланса/транзакций). Здесь
  // просто читаем то, что уже лежит в кеше.

  useEffect(() => {
    // Смена фона
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#0b0d41';
    return () => { document.body.style.backgroundColor = originalBg; };
  }, []);

  useEffect(() => {
    const balanceRef = ref(database, `balance/${chatId}/value`);
    const transactionsRef = ref(database, `transactions/${chatId}`);

    const unsubBalance = onValue(balanceRef, (snapshot) => {
      const data = snapshot.val();
      if (data !== null && data !== undefined) {
        const dataStr = String(data);
        const prevRaw = (() => { try { return localStorage.getItem(balanceKey); } catch (e) { return null; } })();
        if (prevRaw !== dataStr) {
          try { localStorage.setItem(balanceKey, dataStr); } catch (e) { /* ignore */ }
        }
        setBalanceValue(Number(data));
      } else {
        setBalanceValue(0);
      }
    });

    const unsubTransactions = onValue(transactionsRef, (snapshot) => {
      const val = snapshot.val();
      let arr = [];
      if (val !== null && val !== undefined) {
        if (Array.isArray(val)) {
          arr = val;
        } else if (typeof val === 'object') {
          arr = Object.keys(val).map(k => ({ id: k, ...val[k] }));
        }
      }
      cacheTransactions(chatId, arr);
      setTransactions(arr);
    });

    return () => {
      unsubBalance();
      unsubTransactions();
    };
  }, [chatId, balanceKey]);

  return (
    <div className="main-page" onScroll={handleMainScroll}>
      <div className="main-content">
      <div className="hero-con">
        {/* Градієнт «Відкрити картку» — проявляється разом зі свайпом */}
        {pagerCards.map((card, i) => (
          <div
            key={card.id}
            className={`hero-bg-layer hero-bg-${card.type}`}
            ref={(el) => { heroBgRefs.current[i] = el; }}
          />
        ))}
        <div
          className="hero-bg-layer hero-bg-alt"
          ref={(el) => { heroBgRefs.current[pageCount - 1] = el; }}
        />
        <div className="header-con" ref={headerConRef}>
        <div className="leftside">
            <ImageWithCache
              className='avatar-m'
              src={userPhoto || '/mono-avatar.webp'}
              alt="Avatar"
              onClick={() => setAvatarModalOpen(true)}
            />
            <ImageWithCache className='noti-m' src="/noti.png" alt="Noti" />
        </div>
        <div className="rightsite">
            {/* Кешбек, кіт і статистика — окремі кнопки (раніше одна картинка header.png) */}
            <button type="button" className="header-action header-cashback">
              <ImageWithCache className="header-icon header-icon--gift" src="/header-icons/gift.png" alt="" />
              <span className="header-cashback-sum">71.86 ₴</span>
            </button>
            <span className="header-divider" />
            <button type="button" className="header-action">
              <ImageWithCache className="header-icon header-icon--cat" src="/header-icons/cat.png" alt="Кіт" />
            </button>
            <button
              type="button"
              className="header-action header-action--stats"
              onClick={() => navigate('/statistics', { state: { cardId: pagerCards[pagerRef.current.index]?.id } })}
            >
              <ImageWithCache className="header-icon header-icon--stats" src="/header-icons/stats.png" alt="Статистика" />
            </button>
        </div>
      </div>
      {/* Свайп між картками, як у monobank: сторінки (баланс/заголовок,
          кнопки, виписка) їдуть на всю ширину екрана, а картки — окремою
          стрічкою з меншим кроком, тож сусідня визирає з краю. Плашка під
          картками стоїть на місці. */}
      <div
        className="card-pager"
        ref={cardPagerRef}
        onPointerDown={handlePagerPointerDown}
        onPointerMove={handlePagerPointerMove}
        onPointerUp={handlePagerPointerUp}
        onPointerCancel={handlePagerPointerUp}
        onClickCapture={handlePagerClickCapture}
      >
        <div className="card-pages" ref={cardPagesRef}>

        {pagerCards.map((card, i) => {
          const isMain = card.id === MAIN_CARD_ID;
          return (
            <div className="card-page" key={card.id} ref={(el) => { cardPageRefs.current[i] = el; }}>
              <div className="card-page-head">
                <div className="balance-con">
                  <ImageWithCache className='plus-m' src={btnSrc(card.type, 'plus', '/plus.png')} alt="Plus" />
                  <p className='balance-p' onClick={handleBalanceTap}>
                    {isMain ? maskBalance(formatBalance(displayBalance)) : (
                      <AnimatedNumber
                        value={Number(card.balance) || 0}
                        paused={balancesPaused}
                        format={(v) => maskBalance(formatMoney(v, card.currency))}
                      />
                    )}
                  </p>
                </div>
              </div>
              <div className="card-page-space" />
              <div className={`main-btns ${BUY_CURRENCY[card.currency] ? 'main-btns--four' : ''}`}>
                <div className="main-btn" onClick={() => premium && setTransferFrom(card.id)}>
                    <ImageWithCache className='main-img-m' src={btnSrc(card.type, 'card-btn', '/card-btn.png')} alt="Card" />
                     <p className='main-m'>Переказати <br /> на картку</p>
                </div>
                 <div className="main-btn">
                    <ImageWithCache className='main-img-m' src={btnSrc(card.type, 'iban-btn', '/iban-btn.png')} alt="Iban" />
                     <p className='main-m'>Платіж <br /> за IBAN</p>
                </div>
                {BUY_CURRENCY[card.currency] && (
                  <div className="main-btn" onClick={() => premium && setBuyFor(card)}>
                    <ImageWithCache className='main-img-m' src={btnSrc(card.type, 'buy-btn', BUY_CURRENCY[card.currency].icon)} alt="Buy" />
                    <p className='main-m'>Купити <br /> {BUY_CURRENCY[card.currency].label}</p>
                  </div>
                )}
                 <div className="main-btn">
                    <ImageWithCache className='main-img-m' src={btnSrc(card.type, 'another-btn', '/another-btn.png')} alt="Another" />
                     <p className='main-m'>Інші <br /> платежі</p>
                </div>
              </div>
              <Transactions
                transactions={isMain ? transactions : card.transactions}
                cardId={card.id}
                currency={isMain ? 'UAH' : card.currency}
              />
            </div>
          );
        })}

        <div className="card-page" ref={(el) => { cardPageRefs.current[pageCount - 1] = el; }}>
          <div className="card-page-head">
            <div className="new-card-text">
              <p className="new-card-title">Відкрити картку</p>
              <p className="new-card-sub">Ви можете відкрити додаткові картки<br />в гривні, доларах та євро</p>
            </div>
          </div>
          <div className="card-page-space" />
          <div className="new-card-section">
            <button type="button" className="new-card-btn" onClick={openNewCard}>Відкрити картку</button>
            <StatementPlaceholder className="new-card-statement">
              <p className="empty-statement-title">Коли відкриєте нову картку,<br />тут буде її виписка 🏄‍♂️</p>
            </StatementPlaceholder>
          </div>
        </div>
        </div>
        {/* Зона карток і плашки — поверх сторінок і не їде разом з ними;
            картки в ній рухаються окремою стрічкою */}
        <div className="card-pager-fixed">
          <div className="card-track" ref={cardTrackRef}>
            {pagerCards.map((card, i) => {
              const isMain = card.id === MAIN_CARD_ID;
              return (
                <div className="card-track-item" key={card.id}>
                  <CardDesign
                    cardNumber={isMain ? userCardNumber : card.number}
                    holderName={generateCardholderName(userPib)}
                    variant={isMain ? undefined : card.type}
                    label={currencyLabel(card.currency)}
                    // тап по активній картці — попап, по сусідній — гортаємо до неї
                    onOpen={(el) => (pagerRef.current.index === i ? setCardPopup({ el, card }) : goToCardSlide(i))}
                    hidden={cardPopup?.card.id === card.id}
                  />
                </div>
              );
            })}
            <div className="card-track-item">
              {/* та сама розмітка, що й у CardDesign — щоб геометрія
                  нахилу збігалась з основною карткою один в один */}
              <div className="mono-card-wrap">
                <div className="mono-card-stack new-card-stack">
                  <div className="mono-card-backdrop" />
                  <div
                    className="mono-card new-card"
                    onClick={() => (pagerRef.current.index === pageCount - 1 ? openNewCard() : goToCardSlide(pageCount - 1))}
                  >
                    <CardDepth />
                    <div className="new-card-plus" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="all-cards-con">
            <div className="cards-btn card-pager-pill" ref={pagerPillRef}>
              <div className="card-pager-pill-label" onClick={() => setAllCardsOpen(true)}>
                <ImageWithCache className='cards-img-m' src="/cards.png" alt="Cards" />
                <p className='cards-m'>Усi картки</p>
              </div>
              <div className="card-pager-pill-dots">
                {/* позиції і масштаб — з applyPagerDots */}
                <div className="card-pager-window">
                  {Array.from({ length: pageCount }, (_, i) => (
                    <span
                      key={i}
                      ref={(el) => { pagerDotRefs.current[i] = el; }}
                      className={`card-pager-item ${i === 0 ? 'active' : ''}`}
                      style={{ transform: `translate3d(${i * PAGER_DOT_STEP}px, 0, 0) scale(${i < 2 ? 1 : 0})` }}
                      onClick={() => goToCardSlide(i)}
                    >
                      {i === pageCount - 1
                        ? <span className="card-pager-plus">+</span>
                        : <span className="card-pager-dot" />}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      <div className="background">
        <div className="limits-abroad-card">
            <div className="limits-abroad-item">
                <ImageWithCache className="limits-abroad-icon" src="/limits-icon.webp" alt="" />
                <p className="limits-abroad-text">Ліміти та<br />обмеження</p>
            </div>
            <div className="limits-abroad-divider"></div>
            <div className="limits-abroad-item">
                <ImageWithCache className="limits-abroad-icon" src="/home-icon.webp" alt="" />
                <p className="limits-abroad-text">Тим, хто за<br />кордоном</p>
            </div>
        </div>
        <div className="nbu-con">
        <div className="nbu-header">
            <p className='nbu-header-m'>Корисне</p>
        </div>
        <div className="exchange-con">
            <div className="usd-exchange">
                <div className="usd-circle">
                    <div className="flag">🇺🇸</div>
                </div>
                <div className="usd-text-con">
                    <p className='nbu-text-m'>Долар США</p>
                    <p className='nbu-text-limit-m'>{formatRate(rates.usdBuy)} / {formatRate(rates.usdSell)}</p>
                </div>
            </div>
            <div className="eur-exchange">
                <div className="eur-circle">
                    <div className="flag">🇪🇺</div>
                </div>
                <div className="eur-text-con">
                    <p className='nbu-text-m'>Євро</p>
                    <p className='nbu-text-limit-m'>{formatRate(rates.eurBuy)} / {formatRate(rates.eurSell)}</p>
                </div>
            </div>
        </div>
        <div className="more-grid">
            <div className="more-grid-first">
              <div className="more-element">
                <ImageWithCache className='more-img-m' src="/icons/headphones.svg" alt="Headphones" />
                 <p className='more-text-m'>Служба <br />пiдтримки</p>
            </div>
            <div className="more-element">
                <ImageWithCache className='more-img-m' src="/icons/question.png" alt="Question" />
                 <p className='more-text-m'>Поширені <br />запитання</p>
            </div>
            <div className="more-element">
                <ImageWithCache className='more-img-m' src="/icons/scaner.svg" alt="Scaner" />
                 <p className='more-text-m'>Сканер<br /> QR-коду</p>
            </div>
            </div>
            <div className="more-grid-second">
              <div className="more-element">
                <ImageWithCache className='more-img-m' src="/icons/printer.png" alt="Printer" />
                 <p className='more-text-m'>Виписки та довідки</p>
            </div>
            <div className="more-element">
                <ImageWithCache className='more-img-m' src="/icons/document.png" alt="Document" />
                 <p className='more-text-m'>Умови і тарифи</p>
            </div>
            </div>
        </div>
        </div>
        <div className="market-card">
            <p className="market-title">В маркеті від 10 платежів на все 🔥</p>
            <div className="market-grid">
                <div className="market-item">
                    <div className="market-icon-tile market-icon-1">
                        <ImageWithCache className="market-icon-img" src="/market-icons/phone.webp" alt="Смартфони" />
                    </div>
                    <p className="market-item-text">Смартфони<br />та гаджети</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-2"><ImageWithCache className="market-icon-img" src="/market-icons/tv.webp" alt="Телевізори" /></div>
                    <p className="market-item-text">Телевізори</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-3">
                        <ImageWithCache className="market-icon-img" src="/market-icons/ipad.webp" alt="Планшети" />
                    </div>
                    <p className="market-item-text">Планшети</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-4">
                        <ImageWithCache className="market-icon-img" src="/market-icons/pods.webp" alt="Навушники" />
                    </div>
                    <p className="market-item-text">Навушники</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-5">
                        <ImageWithCache className="market-icon-img" src="/market-icons/macbook.webp" alt="Ноутбуки" />
                    </div>
                    <p className="market-item-text">Ноутбуки</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-6">
                        <ImageWithCache className="market-icon-img" src="/market-icons/futo.webp" alt="Побутова техніка" />
                    </div>
                    <p className="market-item-text">Побутова<br />техніка</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-7">
                        <ImageWithCache className="market-icon-img" src="/market-icons/ring.webp" alt="Прикраси та годинники" />
                    </div>
                    <p className="market-item-text">Прикраси<br />та годинники</p>
                </div>
                <div className="market-item">
                    <div className="market-icon-tile market-icon-more"><span>99+</span></div>
                    <p className="market-item-text">Показати<br />більше</p>
                </div>
            </div>
        </div>
      </div>
      </div>
      {avatarModalOpen && createPortal((
        <div
          className={`avatar-modal-overlay ${avatarModalClosing ? 'closing' : ''}`}
          onClick={closeAvatarModal}
        >
          <div
            className={`avatar-modal-sheet ${avatarModalClosing ? 'closing' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="avatar-modal-content">
              <div className="avatar-modal-section">
              <div id='avatar-documents-button' className="avatar-modal-element" onClick={openDocumentsModal}>
                <div className="avatar-modal-icon">
                  <ImageWithCache className='avatar-modal-img-m' src="/badge.svg" alt="Badge" />
                </div>
                <p className='avatar-modal-text-m'>Особисті дані та документи</p>
              </div>
              <div className="avatar-underline-container">
                <div className="avatar-under"></div>
                <div className="avatar-underline"></div>
              </div>
              <div id='avatar-settings-button' className="avatar-modal-element" onClick={openSettingsModal}>
                <div className="avatar-modal-icon">
                  <ImageWithCache className='avatar-modal-img-m' src="/settings-avatar.svg" alt="Settings" />
                </div>
                <p className='avatar-modal-text-m'>Налаштування застосунку</p>
              </div>
            </div>
            <div className="avatar-modal-section">
              <div className="avatar-modal-element">
                <div className="avatar-modal-icon">
                  <ImageWithCache className='avatar-modal-photo-m' src={userPhoto || '/mono-avatar.webp'} alt="Avatar" />
                </div>
                <div className="avatar-modal-side">
                <div className="avatar-text-container">
                  <p className='avatar-modal-text-m'>{userPib || 'Користувач'}</p>
                  <p className='avatar-modal-text-m second-text'>Особисті картки</p>
                </div>
                <ImageWithCache className='avatar-modal-img-m' src="/success-mark.svg" alt="Success" />
                </div>
              </div>
              <div className="avatar-underline-container">
                <div className="avatar-under"></div>
                <div className="avatar-underline"></div>
              </div>
              <div className="avatar-modal-element">
                <div className="avatar-modal-icon fop-icon">
                  <ImageWithCache className='avatar-modal-img-m' src="/building.svg" alt="Building" />
                </div>
                <div className="avatar-modal-side">
                  <p className='avatar-modal-text-m'>ФОП</p>
                  <div className="avatar-fop-button"><span>Детальніше</span></div>
                </div>
              </div>
            </div>
          </div>
            </div>
        </div>
      ), document.body)}

      {allCardsOpen && (
        <AllCardsPopup
          cards={pagerCards.map((card) => (card.id === MAIN_CARD_ID ? {
            id: card.id,
            name: 'Чорна картка',
            icon: '/card-icons/black.png',
            last4: (userCardNumber || '4441000000003070').slice(-4),
            balance: maskBalance(formatBalance(displayBalance)),
          } : {
            id: card.id,
            name: card.name,
            icon: cardIcon(card.type),
            last4: String(card.number || '').slice(-4),
            balance: maskBalance(formatMoney(card.balance, card.currency)),
          }))}
          onReorder={(order) => saveCardsOrder(chatId, order)}
          onSelectCard={(cardId) => {
            const idx = pagerCards.findIndex((c) => c.id === cardId);
            if (idx !== -1) goToCardSlide(idx);
          }}
          onClose={() => setAllCardsOpen(false)}
          onOpenNewCard={openNewCard}
        />
      )}
      {openCardOpen && (
        <OpenCardScreen
          cards={openedCards}
          onClose={() => setOpenCardOpen(false)}
          onCardOpened={handleCardOpened}
        />
      )}
      {buyFor && (
        <BuyCurrencyScreen
          target={buyFor}
          cards={pagerCards}
          fxSpent={fxSpentThisMonth([transactions, ...openedCards.map((c) => c.transactions)])}
          onClose={() => setBuyFor(null)}
        />
      )}
      {transferFrom && (
        <TransferScreen
          cards={pagerCards}
          fxSpent={fxSpentThisMonth([transactions, ...openedCards.map((c) => c.transactions)])}
          fromCardId={transferFrom}
          onClose={() => setTransferFrom(null)}
        />
      )}
      {cardPopup && (
        <CardPopup
          originEl={cardPopup.el}
          {...(cardPopup.card.id === MAIN_CARD_ID ? {
            cardNumber: userCardNumber,
            holderName: generateCardholderName(userPib),
            dailyLimit: DEFAULT_DAILY_LIMIT.UAH,
          } : {
            cardNumber: cardPopup.card.number,
            holderName: generateCardholderName(userPib),
            variant: cardPopup.card.type,
            currency: cardPopup.card.currency,
            label: currencyLabel(cardPopup.card.currency),
            dailyLimit: cardPopup.card.dailyLimit ?? DEFAULT_DAILY_LIMIT[cardPopup.card.currency] ?? 2000,
            dailySpent: cardPopup.card.dailySpent,
            title: cardPopup.card.name,
            secrets: { expiry: cardPopup.card.expiry, cvv: cardPopup.card.cvv },
            miniIcon: cardIcon(cardPopup.card.type),
            isMain: false,
            onCloseCard: () => closeCard(chatId, cardPopup.card.id, cardsOrder)
              .catch(() => window.alert('Не вдалося закрити картку. Спробуйте ще раз')),
          })}
          onClosed={() => setCardPopup(null)}
        />
      )}

      {documentsModalOpen && createPortal((
        <div className={`documents-modal ${documentsModalClosing ? 'closing' : ''}`}>
          <button type="button" className="documents-back-btn" onClick={closeDocumentsModal}>
            <img src="/arrow-back.png" alt="Назад" />
          </button>

          <h1 className="documents-title">Особисті дані та документи</h1>

          <div className="documents-photo-card">
            <ImageWithCache className="documents-photo" src={userPhoto || '/mono-avatar.webp'} alt="Avatar" />
            <p className="documents-photo-btn">Поставити нове фото</p>
          </div>

          <div className="documents-list">
            <div className="documents-row">
              <img className="documents-row-icon" src="/avatar-name.png" alt="" />
              <div className="documents-row-text">
                <p className="documents-row-label">Як до вас звертатися?</p>
                <p className="documents-row-value">{userPib || '—'}</p>
              </div>
            </div>
            <div className="documents-row">
              <img className="documents-row-icon" src="/avatar-number.png" alt="" />
              <div className="documents-row-text">
                <p className="documents-row-label">Телефон</p>
                <p className="documents-row-value">{generatePhone(chatId)}</p>
              </div>
            </div>
            <div className="documents-row">
              <img className="documents-row-icon" src="/avatar-email.png" alt="" />
              <div className="documents-row-text">
                <p className="documents-row-label">Ваша ел. пошта</p>
                <p className="documents-row-value">{generateEmail(userPib)}</p>
              </div>
            </div>
            <div className="documents-row">
              <img className="documents-row-icon" src="/avatar-documents.png" alt="" />
              <div className="documents-row-text">
                <p className="documents-row-value">Документи</p>
                <p className="documents-row-hint">Надайте нові або перегляньте надані</p>
              </div>
            </div>
            <div className="documents-row">
              <img className="documents-row-icon" src="/avatar-profile.png" alt="" />
              <p className="documents-row-value">Персональні дані</p>
            </div>
          </div>
        </div>
      ), document.body)}

      {settingsModalOpen && createPortal((
        <div className={`documents-modal ${settingsModalClosing ? 'closing' : ''}`}>
          <button type="button" className="documents-back-btn" onClick={closeSettingsModal}>
            <img src="/arrow-back.png" alt="Назад" />
          </button>

          <h1 className="documents-title">Налаштування застосунку</h1>

          <div className="settings-section">
            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Вхід за допомогою Apple</p>
                <p className="settings-row-subtitle">Для авторизації без номера телефону</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.appleSignIn ? 'on' : ''}`}
                onClick={() => toggleAppSetting('appleSignIn')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Вхід через Google</p>
                <p className="settings-row-subtitle">Для авторизації без номера телефону</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.googleSignIn ? 'on' : ''}`}
                onClick={() => toggleAppSetting('googleSignIn')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Показувати копійки</p>
                <p className="settings-row-subtitle">Суми у форматі 00.00</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.showCents ? 'on' : ''}`}
                onClick={() => toggleAppSetting('showCents')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Отримувати одяг на кота</p>
                <p className="settings-row-subtitle">Та інші нагороди від користування карткою</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.catRewards ? 'on' : ''}`}
                onClick={() => toggleAppSetting('catRewards')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Показувати калькулятор</p>
                <p className="settings-row-subtitle">На екрані введення суми</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.showCalculator ? 'on' : ''}`}
                onClick={() => toggleAppSetting('showCalculator')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-divider" />

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Режим інкогніто</p>
                <p className="settings-row-subtitle">Баланси буде приховано</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${balancesHidden ? 'on' : ''}`}
                onClick={toggleBalancesHidden}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Показувати в контактах</p>
                <p className="settings-row-subtitle">У інших користувачів</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.showInContacts ? 'on' : ''}`}
                onClick={() => toggleAppSetting('showInContacts')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>

            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Заборона відновлення ПІН</p>
                <p className="settings-row-subtitle">
                  Якщо ви забудете ПІН, відновити доступ до аккаунту можна буде лише після звернення у підтримку для проходження відеоверифікації
                </p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.pinRecoveryBlock ? 'on' : ''}`}
                onClick={() => toggleAppSetting('pinRecoveryBlock')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>
          </div>

          <div className="settings-section">
            <div className="settings-value-row">
              <p className="settings-value-label">Тема застосунку</p>
              <p className="settings-value-text">Темна</p>
            </div>
            <div className="settings-value-row">
              <p className="settings-value-label">Мова інтерфейсу</p>
              <p className="settings-value-text">Українська</p>
            </div>
            <div className="settings-row">
              <div className="settings-row-text">
                <p className="settings-row-title">Системний розмір тексту</p>
                <p className="settings-row-subtitle">Масштабувати текст залежно від налаштувань системи (потрібен перезапуск застосунку)</p>
              </div>
              <button
                type="button"
                className={`settings-toggle ${appSettings.systemTextSize ? 'on' : ''}`}
                onClick={() => toggleAppSetting('systemTextSize')}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>
          </div>

          <div className="settings-logout-btn" onClick={logout}>Вийти</div>
        </div>
      ), document.body)}
      <div className="block"></div>
    </div>
  );
};

export default MainPage;
